import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../redux/store';
import { REGIMES_CODE } from '../../../@types/Regime.type';
import { RegimeFFT } from '../../../@types/Products.type';
import { getSalesPeriodsAdquired } from '../../../services/sales.service';
import { logger } from '../../../shared/utils/logger';

const REGIME_BY_DECLARATION_TYPE = {
	platec: RegimeFFT[REGIMES_CODE.PLATAFORMAS_TECNOLOGICAS],
	resico: RegimeFFT[REGIMES_CODE.RESICO],
} as const;

const PURCHASED_PERIODS_QUERY_KEY = ['regularization', 'purchasedPeriods'] as const;

const NO_PERIODS: ReadonlySet<string> = new Set();

export const purchasedPeriodKey = (type: string, year: number, month: number) =>
	`${type}::${year}-${String(month).padStart(2, '0')}`;

const fetchPurchasedPeriodKeys = async (): Promise<string[]> => {
	const entries = Object.entries(REGIME_BY_DECLARATION_TYPE);
	const results = await Promise.allSettled(
		entries.map(async ([type, regime]) => {
			const response = await getSalesPeriodsAdquired({ regime, period_type: 'monthly' });
			return (response?.monthly ?? []).map((period: { year: number; month: number }) =>
				purchasedPeriodKey(type, period.year, period.month),
			);
		}),
	);

	const failed = results.flatMap((result, index) => (result.status === 'rejected' ? [{ type: entries[index][0], reason: result.reason }] : []));
	// Every regime failed: surface the error so the query retries instead of caching an empty list.
	if (failed.length === results.length) throw failed[0].reason;
	// One regime failed: keep what the other returned, so a bought period is still protected from re-buying.
	for (const { type, reason } of failed) logger.warn(`Could not read the purchased ${type} periods:`, reason);

	return results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
};

/**
 * The monthly periods the user already bought (paid services and delivered
 * filings, from the sales engine), keyed with `purchasedPeriodKey`. Only users
 * the declarations engine has no grid for need it: with a grid, a bought
 * period already arrives as covered in `taxProfile.declarationsOverview`.
 */
export const usePurchasedPeriods = (): ReadonlySet<string> => {
	const hasNoGrid = useSelector((state: RootState) => state.taxProfile.declarationsOverview?.length === 0);
	const { data } = useQuery({
		queryKey: PURCHASED_PERIODS_QUERY_KEY,
		queryFn: fetchPurchasedPeriodKeys,
		enabled: hasNoGrid,
		staleTime: 60_000,
	});
	return useMemo(() => (hasNoGrid && data ? new Set(data) : NO_PERIODS), [hasNoGrid, data]);
};
